<template>
    <b-overlay
        :show="status.isWaiting"
        variant="light"
        spinner-variant="primary"
    >
        <div class="bg-white rounded">
            <b-row class="justify-content-lg-center" no-gutters>
                <b-col :lg=9>
                    <b-table id="tasksTable" class="mb-1" responsive outlined borderless striped hover show-empty :empty-text="$t('app_table_empty_text')" :items="filterItems()" :fields="fields" :per-page="perPage" :current-page="currentPage">
                        <template v-slot:head(selected)>
                            <b-form-checkbox size="lg" v-model="itemsChecked" @change="checkAll" />
                        </template>
                        <template v-slot:head(customer)>
                            {{ $t('task_label_customer') }}
                        </template>
                        <template v-slot:head(washstation)>
                            {{ $t('task_label_wash_station') }}
                        </template>
                        <template v-slot:head(startTime)>
                            {{ $t('task_label_start_time') }}
                        </template>
                        <template v-slot:head(endTime)>
                            {{ $t('task_label_end_time') }}
                        </template>
                        <template v-slot:head(worker)>
                            {{ $t('task_label_worker') }}
                        </template>
                        <template v-slot:head(description)>
                            {{ $t('task_label_description') }}
                        </template>
                        <template v-slot:head(invoice)>
                            {{ $t('task_label_invoice') }}
                        </template>
                        <template v-slot:head(confirmedAt)>
                            {{ $t('task_label_confirmed_at') }}
                        </template>
                        <template v-slot:head(actions)>
                            {{ $t('task_label_actions') }}
                        </template>
                        <template v-slot:cell(selected)="{ item }">
                            <b-form-checkbox size="lg" :value="item" v-model="selectedItems" @change="itemCheck" />
                        </template>
                        <template v-slot:cell(startTime)="{ item }">
                            {{ $moment(item.startTime).format(dateFormat) }}
                        </template>
                        <template v-slot:cell(endTime)="{ item }">
                            {{ $moment(item.endTime).format(dateFormat) }}
                        </template>
                        <template v-slot:cell(customer)="{ item }">
                            <p v-if="!item.customer">
                                <b-icon icon="exclamation-triangle-fill" scale="1.5" variant="warning" class="mr-1" />
                                {{ $t('task_new_customer') }}
                            </p>
                            <p v-else>{{ item.customer.name }}</p>
                        </template>
                        <template v-slot:cell(washstation)="{ item }">
                            {{ item.washstation.name }}
                        </template>
                        <!--
                        <template v-slot:cell(worker)="{ item }">
                            {{ item.worker.firstname }} {{ item.worker.lastname }}
                        </template>
                        -->
                        <template v-slot:cell(description)="{ item }">
                            {{ item.description ? trimText(item.description) : "-" }}
                        </template>
                        <template v-slot:cell(invoice)="{ item }">
                            {{ item.invoice && item.invoiceSentAt ? $moment(item.invoiceSentAt).format(dateFormat) : "-" }}
                        </template>
                        <template v-slot:cell(confirmedAt)="{ item }">
                            {{ item.confirmedAt ? $moment(item.confirmedAt).format(dateFormat) : $t('app_no') }}
                        </template>
                        <template v-slot:cell(actions)="{ item }">
                            <a :href="`/task/${item.id}`" type="button" class="btn btn-sm btn-info">{{ $t('task_button_view') }}</a>
                        </template>
                    </b-table>
                </b-col>
                <b-col :lg=3>
                    <b-card no-body class="p-0 mb-2" v-if="selectedItems.length">
                        <b-card-header header-tag="header" class="p-0" role="tab">
                            <b-button block v-b-toggle.tab2 variant="light">
                                {{ $t('task_selected_items') }}: <b-badge pill variant="primary">{{ selectedItems.length }}</b-badge>
                            </b-button>
                        </b-card-header>
                        <b-collapse id="tab2" role="tabpanel">
                            <b-card-body class="p-2 text-center">
                                <b-button variant="secondary" @click="unselectAll()">{{ $t('task_button_remove_selection') }}</b-button>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                    <b-card no-body class="p-0">
                        <b-card-header header-tag="header" class="p-0" role="tab">
                            <b-button block v-b-toggle.tab1 variant="light">{{ $t('app_table_header_filters') }}</b-button>
                        </b-card-header>
                        <b-collapse id="tab1" visible role="tabpanel">
                            <b-card-body class="p-3">
                                <b-form-group label-size="sm" :label="$t('task_label_filter_search')" label-for="filterSearch" label-class="mb-0">
                                    <b-form-input v-model="filterSearch" id="filterSearch" :placeholder="$t('task_label_filter_search_placeholder')" size="sm" />
                                </b-form-group>
                                <b-form-group label-size="sm" :label="$t('task_label_filter_wash_station')" label-for="filterWashStation" label-class="mb-0">
                                    <v-select
                                        :options="washStationFilterOptions(washStations)"
                                        :reduce="washStation => washStation.val"
                                        label="name"
                                        v-model="filterWashStation"
                                        :clearable="false"
                                    >
                                        <template #search="{attributes, events}">
                                            <input
                                                class="vs__search"
                                                :required="!filterWashStation"
                                                v-bind="attributes"
                                                v-on="events"
                                            />
                                        </template>
                                        <div slot="no-options">{{ $t('app_vselect_no_options_found') }}</div>
                                    </v-select>
                                </b-form-group>
                                <b-form-group label-size="sm" :label="$t('task_label_filter_worker')" label-for="filterWorker" label-class="mb-0">
                                    <v-select
                                        :options="workerFilterOptions(filterWorkers(workers))"
                                        :reduce="worker => worker.val"
                                        label="label"
                                        v-model="filterWorker"
                                        :clearable="false"
                                    >
                                        <template #search="{attributes, events}">
                                            <input
                                                class="vs__search"
                                                :required="!filterWorker"
                                                v-bind="attributes"
                                                v-on="events"
                                            />
                                        </template>
                                        <div slot="no-options">{{ $t('app_vselect_no_options_found') }}</div>
                                    </v-select>
                                </b-form-group>
                                <b-form-group label-size="sm" :label="$t('task_label_filter_customer')" label-for="filterCustomer" label-class="mb-0">
                                    <v-select
                                        :options="customerFilterOptions(customers)"
                                        :reduce="worker => worker.val"
                                        label="name"
                                        v-model="filterCustomer"
                                        :clearable="false"
                                    >
                                        <template #search="{attributes, events}">
                                            <input
                                                class="vs__search"
                                                :required="!filterCustomer"
                                                v-bind="attributes"
                                                v-on="events"
                                            />
                                        </template>
                                        <div slot="no-options">{{ $t('app_vselect_no_options_found') }}</div>
                                    </v-select>
                                </b-form-group>
                                <b-form-group v-if="checkRole(user, 'ROLE_ADMIN')" label-size="sm" :label="$t('task_label_filter_only_unconfirmed')" label-for="filterOnlyUnconfirmed" label-class="mb-0">
                                    <b-form-checkbox v-model="filterOnlyUnconfirmed" id="filterOnlyUnconfirmed" switch />
                                </b-form-group>
                                <b-form-group>
                                    <b-button @click="resetFilters" size="sm">{{ $t('app_table_button_filter_reset') }}</b-button>
                                </b-form-group>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </b-col>
            </b-row>
            <b-row class="justify-content-lg-center pl-2 pr-2">
                <b-col>
                    <label>
                        <small><b>{{ $t('app_table_label_per_page') }}</b></small>
                        <b-form-select size="sm" v-model="perPage" :options="perPageOptions" />
                    </label>
                </b-col>
                <b-col class="d-flex">
                    <b-pagination class="my-auto ml-auto" align="right" v-model="currentPage" :total-rows="rows" :per-page="perPage" aria-controls="tasksTable" /> 
                </b-col>
            </b-row>
        </div>
		<b-row class="justify-content-lg-center">
			<b-col>
                <hr />
                <a :href="`/task/new`" type="button" class="btn btn-primary mr-2">{{ $t('task_button_create') }}</a>
                <b-button 
                    v-if="checkRole(user, 'ROLE_ADMIN')"
                    :disabled="selectedItems.length ? false : true" 
                    variant="info" 
                    @click="showConfirmModal()"
                >{{ $t('task_button_confirm') }}</b-button>
			</b-col>
		</b-row>
        
		<b-modal 
            ref="confirmModal" 
            size="lg" 
            :title="$t('task_confirm_modal_title')" 
        >
            <b-alert show variant="warning" v-if="warnTaskCustomer()">
                {{ $t('task_confirm_modal_warning_customer') }}
            </b-alert>
            <b-list-group class="overflow-auto selected_items_scroll_list">
                <b-list-group-item 
                    v-for="(item, index) in selectedItems" 
                    :key="item.id"
                    class="d-flex justify-content-between align-items-center"
                    :variant="confirmModalListItemVariant(item)"
                >
                    {{ $moment(item.startTime).format(dateFormat) }} - 
                    {{ item.worker.firstname }} {{ item.worker.lastname }} - 
                    {{ item.customer && item.customer.name ? item.customer.name : $t('task_new_customer') }} - 
                    {{ item.washstation.name }}
                    <br/>
                    {{ item.confirmedAt ? `${$t('task_label_confirmed_at')}: ${$moment(item.confirmedAt).format(dateFormat)}` : '' }}
                    <b-button variant="danger" @click="unselectItem(index)"><b-icon icon="x" scale="1.25" /></b-button>
                </b-list-group-item>
            </b-list-group>
            <template #modal-footer>
                <b-button variant="secondary" @click="confirmTasks(null)">
                    {{ $t('task_modal_button_unconfirm') }}
                </b-button>
                <b-button variant="primary" @click="confirmTasks(Date.now())">
                    {{ $t('task_modal_button_confirm') }}
                </b-button>
            </template>
		</b-modal>
    </b-overlay>
</template>

<script>
export default {
    name: 'TaskIndex',
    props: [
        'user',
        'checkRole',
        'tasks',
        'status',
        'customers',
        'washStations',
        'workers',
		'filterWorkers',
        'taskConfirmCall',
        'dateFormat',
        'findById',
        'checkTaskCustomer',
        'getTasks',
    ],
    data() {
        return {
            perPageOptions: [ 5, 10, 25, 50, 100 ],
            perPage: 25,
            currentPage: 1,
            itemsChecked: false,
            selectedItems: [],
            fields: this.checkRole(this.user, 'ROLE_ADMIN') ? [
                { key: 'selected', sortable: false },
                { key: 'startTime', sortable: true },
                //{ key: 'endTime', sortable: true },
                {
                    key: 'worker',
                    sortable: true,
                    formatter: (value, key, item) => {
                        return `${item.worker.firstname} ${item.worker.lastname}`
                    },
                    sortByFormatted: true,
                },
                {
                    key: 'customer',
                    sortable: true,
                    formatter: (value, key, item) => {
                        return item.customer ? item.customer.name : this.$t('task_new_customer')
                    },
                    sortByFormatted: true,
                },
                { key: 'washstation', sortable: true },
                //{ key: 'invoice', sortable: true },
                { key: 'confirmedAt', sortable: true },
                //{ key: 'description', sortable: true },
                { key: 'actions', sortable: false },
            ] : [
                { key: 'startTime', sortable: true },
                {
                    key: 'worker',
                    sortable: true,
                    formatter: (value, key, item) => {
                        return `${item.worker.firstname} ${item.worker.lastname}`
                    },
                    sortByFormatted: true,
                },
                {
                    key: 'customer',
                    sortable: true,
                    formatter: (value, key, item) => {
                        return item.customer ? item.customer.name : this.$t('task_new_customer')
                    },
                    sortByFormatted: true,
                },
                { key: 'washstation', sortable: true },
                { key: 'actions', sortable: false },
            ],
            filterSearch: '',
            filterWashStation: 'all',
            filterWorker: 'all',
            filterCustomer: 'all',
            filterOnlyUnconfirmed: true,
        }
    },
    computed: {
        rows() {
            return this.tasks ? this.tasks.length : 0;
        }
    },
    methods: {
        trimText(string) {
            var maxLength = 50;
            if (string.length > maxLength) {
                var trimmedString = string.substring(0, maxLength);
                return trimmedString + '...';
            } else {
                return string;
            }
        },
        itemCheck(checked) {
            if(!checked) {
                this.itemsChecked = false;
            }
        },
        checkAll(checked) {
            if (checked) {
                let items = this.filterItems();
                items.forEach(item => {
                    if (!this.selectedItems.find(selectedItem => selectedItem.id == item.id)) {
                        this.selectedItems.push(item);
                    }
                });
            } else {
                let items = this.filterItems();
                items.forEach(item => {
                    let index = this.selectedItems.findIndex(selectedItem => selectedItem.id == item.id);
                    if (index != -1) {
                        this.selectedItems.splice(index, 1);
                    }
                });
            }
        },
        unselectItem(index) {
            this.itemsChecked = false;
            this.selectedItems.splice(index, 1);
            if (!this.selectedItems.length) {
                this.$refs['confirmModal'].hide();
            }
        },
        unselectAll() {
            this.itemsChecked = false;
            this.selectedItems = [];
        },
        watchSelectedItems(items) {
            if (this.selectedItems.length && items.length && this.selectedItems.length >= items.length) {
                let selected = [];
                this.selectedItems.forEach(item => {
                    selected.push(item.id)
                });
                let filteredItems = [];
                items.forEach(item => {
                    filteredItems.push(item.id)
                });
                selected = selected.sort(function(a, b) {
                    return a - b;
                });
                filteredItems = filteredItems.sort(function(a, b) {
                    return a - b;
                });
                if (JSON.stringify(selected) == JSON.stringify(filteredItems) || this.itemsInclude(selected, filteredItems)) {
                    this.itemsChecked = true;
                } else {
                    this.itemsChecked = false;
                }
            } else {
                this.itemsChecked = false;
            }
        },
        itemsInclude(selected, filtered) {
            let isIncluded = true;
            filtered.forEach(id => {
                if (!selected.includes(id)) {
                    isIncluded = false;
                }
            });
            return isIncluded;
        },
        filterItems() {
            let self = this;
            let items = this.tasks;
            if (items && items.length) {
                let filteredItems = items.filter(function (item) {
                    // washstation filter
                    if (item.washstation.id == self.filterWashStation || self.filterWashStation == 'all') {
                        // worker filter
                        if (item.worker.id == self.filterWorker || self.filterWorker == 'all') {
                            // customer filter
                            if (
                                item.customer && item.customer.id == self.filterCustomer ||
                                item.customer == self.filterCustomer ||
                                self.filterCustomer == 'all'
                            ) {
                                // invoiced filter
                                //if (!item.confirmedAt && self.filterOnlyUnconfirmed || !self.filterOnlyUnconfirmed) {
                                    // search filter
                                    if (self.filterSearch != '') {
                                        let customerName = item.customer ? item.customer.name : self.$t('task_new_customer');
                                        if (
                                            `${item.worker.firstname.toLowerCase()} ${item.worker.lastname.toLowerCase()}`.includes(self.filterSearch.toLowerCase()) || 
                                            `${item.worker.lastname.toLowerCase()} ${item.worker.firstname.toLowerCase()}`.includes(self.filterSearch.toLowerCase()) || 
                                            customerName.toLowerCase().includes(self.filterSearch.toLowerCase())
                                        ) {
                                            return item;
                                        }
                                    } else {
                                        return item;
                                    }
                                //}
                            }
                        }
                    }
                })
                this.watchSelectedItems(filteredItems);
                return filteredItems;
            }
        },
        washStationFilterOptions(washStations) {
			if (washStations && washStations.length) {
				let stations = [{
					val: "all",
					name: this.$t('task_label_filter_all'),
				}];
				washStations.forEach(washStation => {
                    stations.push({
                        val: washStation.id,
                        name: washStation.name,
                    });
				});
				return stations;
			}
        },
        workerFilterOptions(workers) {
			if (workers && workers.length) {
				let fWorkers = [{
                    val: "all",
                    label: this.$t('task_label_filter_all'),
                }];
				workers.forEach(worker => {
                    fWorkers.push({
                        val: worker.id,
                        label: `${worker.firstname} ${worker.lastname}`,
                    });
				});
				return fWorkers;
			}
        },
        customerFilterOptions(customers) {
			if (customers && customers.length) {
				let fCustomers = [
                    {
                        val: "all",
                        name: this.$t('task_label_filter_all'),
                    },
                    {
                        val: null,
                        name: this.$t('task_label_filter_customer_new'),
                    }
                ];
				customers.forEach(customer => {
                    if (customer.isActive) {
                        fCustomers.push({
                            val: customer.id,
                            name: customer.name,
                        });
                    }
				});
				return fCustomers;
			}
        },
        resetFilters() {
            this.filterSearch = '';
            this.filterWashStation = 'all';
            this.filterWorker = 'all';
            this.filterCustomer = 'all';
            this.filterOnlyUnconfirmed = true;
        },
        showConfirmModal() {
            this.$refs['confirmModal'].show();
        },
        confirmTasks(input) {
            this.selectedItems.forEach(task => {
                if (task.customer && task.customer !== 0) {
                    this.taskConfirmCall(task.id, JSON.stringify({
                        confirmedAt: input,
                    }));
                }
            });
            this.unselectAll();
            this.$refs['confirmModal'].hide();
        },
        confirmModalListItemVariant(task) {
            let condition_1 =  !task.customer || task.customer === 0;
            if (condition_1) {
                return 'warning';
            } else {
                return null;
            }
        },
        warnTaskCustomer() {
            var showWarning = false;
            if (this.selectedItems && this.selectedItems.length) {
                this.selectedItems.forEach(task => {
                    if (!task.customer || task.customer === 0) {
                        showWarning = true;
                    }
                });
            }
            return showWarning;
        },
    },
    watch: {
        filterOnlyUnconfirmed(state) {
            this.getTasks(state);
        },
    },
}
</script>
